import { RefObject } from 'react';
import { Recipe } from '../../api/Recipe';
import { deriveHomeRecipeFeedState } from '../../domains/recipes/use-cases/homeRecipeFeed';
import useHomeFilterBar from './useHomeFilterBar';
import useFavoriteToggleAction from '../favorites/useFavoriteToggleAction';

type ToastType = 'success' | 'error' | 'info';

interface UseHomePageControllerOptions {
  displayMode: string;
  gridScrollRef: RefObject<HTMLDivElement | null>;
  listScrollRef: RefObject<HTMLDivElement | null>;
  allRecipes: Recipe[];
  dailySelectionRecipe: Recipe | null;
  hasMore: boolean;
  isRefreshing: boolean;
  loading: boolean;
  isLoadingTags: boolean;
  isLoadingAuthors: boolean;
  selectedTagIds: number[];
  selectedAuthorIds: number[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  clearFilters: () => void;
  setSortField: React.Dispatch<React.SetStateAction<string>>;
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  showToast: (message: string, type?: ToastType) => void;
  isFavorite: (recipeId: string) => boolean;
  addFavorite: (recipe: Recipe) => Promise<void>;
  removeFavorite: (recipeId: string) => Promise<void>;
}

interface UseHomePageControllerResult {
  displayedRecipes: Recipe[];
  showDailySelection: boolean;
  showEmptyState: boolean;
  handleFavoriteToggle: (recipe: Recipe) => Promise<void>;
  filterBarComponentRef: RefObject<any>;
  isFilterBarOpen: boolean;
  onClearFilters: () => void;
  handleFilterBarToggle: () => void;
  handleSortFieldChange: (field: string) => void;
  handleSortDirectionChange: (direction: 'asc' | 'desc') => void;
}

export const useHomePageController = ({
  displayMode,
  gridScrollRef,
  listScrollRef,
  allRecipes,
  dailySelectionRecipe,
  hasMore,
  isRefreshing,
  loading,
  isLoadingTags,
  isLoadingAuthors,
  selectedTagIds,
  selectedAuthorIds,
  sortField,
  sortDirection,
  clearFilters,
  setSortField,
  setSortDirection,
  showToast,
  isFavorite,
  addFavorite,
  removeFavorite,
}: UseHomePageControllerOptions): UseHomePageControllerResult => {
  const {
    filterBarComponentRef,
    isFilterBarOpen,
    onClearFilters,
    handleFilterBarToggle,
    handleSortFieldChange,
    handleSortDirectionChange,
  } = useHomeFilterBar({
    displayMode,
    gridScrollRef,
    listScrollRef,
    selectedTagIds,
    selectedAuthorIds,
    sortField,
    sortDirection,
    clearFilters,
    setSortField,
    setSortDirection,
    showToast,
  });

  const handleFavoriteToggle = useFavoriteToggleAction({
    isFavorite,
    addFavorite,
    removeFavorite,
    showToast,
    successMessages: {
      added: 'Recette ajoutée aux favoris',
      removed: 'Recette retirée des favoris',
    },
  });

  const feedState = deriveHomeRecipeFeedState({
    allRecipes,
    dailySelectionRecipe,
    hasMore,
    isRefreshing,
    loading,
    isLoadingTags,
    isLoadingAuthors,
  });

  return {
    displayedRecipes: feedState.displayedRecipes,
    showDailySelection: feedState.showDailySelection,
    showEmptyState: feedState.showEmptyState,
    handleFavoriteToggle,
    filterBarComponentRef,
    isFilterBarOpen,
    onClearFilters,
    handleFilterBarToggle,
    handleSortFieldChange,
    handleSortDirectionChange,
  };
};

export default useHomePageController;
